import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * Polling Trigger Node Schema (v2.0 - Sub-Node Architecture)
 *
 * Connect any integration node to poll it automatically.
 * The connected node handles its own credentials and configuration.
 * Generic HTTP fallback is available when no node is connected.
 */
const schema: NodePropertySchema = {
  nodeType: 'polling-trigger',
  version: '2.0.0',
  properties: [
    // ═══════════════════════════════════════════════════════════════════════════
    // POLLING CONFIGURATION
    // ═══════════════════════════════════════════════════════════════════════════
    {
      key: 'pollInterval',
      label: 'Poll Interval',
      type: 'options',
      required: true,
      default: '0 */5 * * * *',
      options: [
        { value: '0 */1 * * * *', name: 'Every minute' },
        { value: '0 */5 * * * *', name: 'Every 5 minutes' },
        { value: '0 */15 * * * *', name: 'Every 15 minutes' },
        { value: '0 */30 * * * *', name: 'Every 30 minutes' },
        { value: '0 0 * * * *', name: 'Every hour' },
        { value: '0 0 */6 * * *', name: 'Every 6 hours' },
        { value: '0 0 0 * * *', name: 'Once daily (midnight)' },
      ],
      description: 'How often to poll for new records',
    },
    {
      key: 'lookbackWindow',
      label: 'Lookback Window (hours)',
      type: 'number',
      required: true,
      default: 24,
      description:
        'How far back to look on the first poll (before any state exists)',
    },
    {
      key: 'batchMode',
      label: 'Output Mode',
      type: 'options',
      required: true,
      default: 'batch',
      options: [
        {
          value: 'batch',
          name: 'All Records (single execution with array)',
        },
        {
          value: 'individual',
          name: 'One at a Time (separate execution per record)',
        },
      ],
      description: 'How to output records to the workflow',
    },
    {
      key: 'maxRecordsPerPoll',
      label: 'Max Records per Poll',
      type: 'number',
      required: false,
      default: 100,
      description:
        'Maximum records to process per poll (prevents runaway processing)',
    },
    {
      key: 'continueOnNoRecords',
      label: 'Continue when no records found',
      type: 'boolean',
      default: false,
      description:
        'If enabled, workflow continues even when no new records are found',
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // RESPONSE PARSING
    // ═══════════════════════════════════════════════════════════════════════════
    {
      key: 'recordsPath',
      label: 'Records Path',
      type: 'string',
      required: true,
      default: 'data',
      placeholder: 'data.items',
      description:
        'Path to the records array in the response (e.g., data.items)',
    },
    {
      key: 'dateField',
      label: 'Date Field',
      type: 'string',
      required: true,
      default: 'createdAt',
      placeholder: 'createdAt',
      description:
        'Field name containing the record creation/modification date',
    },
    {
      key: 'idField',
      label: 'ID Field',
      type: 'string',
      required: true,
      default: 'id',
      placeholder: 'id',
      description: 'Unique identifier field for deduplication',
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // GENERIC HTTP FALLBACK (used when no sub-node is connected)
    // ═══════════════════════════════════════════════════════════════════════════
    {
      key: 'useGenericHttp',
      label: 'Use Generic HTTP (no connected node)',
      type: 'boolean',
      default: false,
      description:
        'Enable to configure custom HTTP polling when not using a connected node',
    },
    {
      key: 'httpUrl',
      label: 'URL',
      type: 'string',
      required: false,
      displayOptions: { show: { useGenericHttp: [true] } },
      placeholder: 'https://api.example.com/records',
      description: 'API endpoint to poll. Supports variable interpolation.',
    },
    {
      key: 'httpMethod',
      label: 'Method',
      type: 'options',
      required: false,
      default: 'GET',
      displayOptions: { show: { useGenericHttp: [true] } },
      options: [
        { value: 'GET', name: 'GET' },
        { value: 'POST', name: 'POST' },
      ],
    },
    {
      key: 'httpHeaders',
      label: 'Headers',
      type: 'json',
      required: false,
      displayOptions: { show: { useGenericHttp: [true] } },
      placeholder: `{
  "Authorization": "Bearer {{ $credential.apiKey }}",
  "Content-Type": "application/json"
}`,
    },
    {
      key: 'httpBody',
      label: 'Body',
      type: 'json',
      required: false,
      displayOptions: {
        show: { useGenericHttp: [true], httpMethod: ['POST'] },
      },
      placeholder: `{
  "filter": {
    "createdAfter": "{{ $polling.since }}"
  }
}`,
      description:
        'Request body for POST requests. Use {{ $polling.since }} for the date filter.',
    },
    {
      key: 'httpCredentialId',
      label: 'Credential',
      type: 'credential',
      required: false,
      displayOptions: { show: { useGenericHttp: [true] } },
      description: 'Optional credential for authentication',
    },
    {
      key: 'httpDateParameterName',
      label: 'Date Filter Parameter',
      type: 'string',
      required: false,
      displayOptions: { show: { useGenericHttp: [true] } },
      placeholder: 'created_after',
      description: 'Query parameter name for the "since" date filter',
    },
    {
      key: 'httpDateFormat',
      label: 'Date Format',
      type: 'options',
      required: false,
      default: 'iso8601',
      displayOptions: { show: { useGenericHttp: [true] } },
      options: [
        { value: 'iso8601', name: 'ISO 8601 (2025-02-03T10:30:00Z)' },
        { value: 'unix', name: 'Unix Timestamp (1706958600)' },
        { value: 'unixMs', name: 'Unix Milliseconds (1706958600000)' },
        { value: 'custom', name: 'Custom Format' },
      ],
    },
    {
      key: 'httpDateFormatCustom',
      label: 'Custom Date Format',
      type: 'string',
      required: false,
      displayOptions: {
        show: { useGenericHttp: [true], httpDateFormat: ['custom'] },
      },
      placeholder: 'yyyy-MM-dd HH:mm:ss',
    },
  ],
};

export default schema;
